/*
================================================================================
File: src/App.js
Description: Main application component, handles routing, state, and main layout.
             (Corrected to handle all navigation views)
================================================================================
*/
import React, { useState, useRef, useCallback, useEffect } from "react";
import md5 from "md5";
import { MMKV } from "react-native-mmkv";
import { useAdaptiveMMKV } from "./hooks/useAdaptiveMMKV";
import moment from "moment";

import instance from "./api/axios";
import { views } from "./constants/views";
import AuthForm from "./components/auth/AuthForm";
import Navbar from "./components/navigation/Navbar";
import AIAssistantChat from "./components/chat/AIAssistantChat";
import Modal from "./components/common/Modal";

// Import Page Components
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import HistoryPagesSkip from "./pages/HistoryPages_Skip";
import HistoryPages from "./pages/HistoryPages";
import SellerPages from "./pages/SellerLeads/SellerPages";
import EditLeadPage from "./pages/SellerLeads/EditLeadPage";
import EditLeadPageBlank from "./pages/SellerLeads/EditLeadPageBlank";
import ContractForm from "./pages/SellerLeads/ContractForm";
import QuickScorebaord from "./pages/SellerLeads/QuickScorebaord";

import SellerResponders, {
  SellerRespondersView,
} from "./pages/SellerLeads/SellerResponders";
import InvestorPages from "./pages/InvestorLeads/InvestorPages";
import InvestorResponders, {
  InvestorRespondersView,
} from "./pages/InvestorLeads/InvestorResponders";
import Skiptracing from "./pages/Skiptracing";
import MyInformation from "./pages/Settings/MyInformation";
import MyCompany from "./pages/Settings/MyCompany";
import ContractOptions from "./pages/Settings/ContractOptions";
import MyInvestor from "./pages/Settings/MyInvestor";
import SqueezSite from "./pages/SqueezeSite";
import Training from "./pages/Training";
import Ecommerce from "./pages/Ecommerce";
import Links from "./pages/Links";
import SupportPage from "./pages/SupportPage";

export const storage = new MMKV();
const url = "https://app.bigdogtools.com/sites"; // API base URL needed for images

export default function App() {
  const [user, setUser] = useAdaptiveMMKV("user.Feed", storage);
  const [currentView, setCurrentView] = useState(() =>
    user?.app_id ? views.dashboard : views.login,
  );
  const [isLoggedIn, setIsLoggedIn] = useState(!!user?.app_id);
  const [activeLead, setActiveLead] = useState(null);
  const [activeResponder, setActiveResponder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // State and refs for Header Menu and Modals
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const menuRef = useRef([]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside of any of the menu containers
      const isOutside = menuRef.current.every(
        (ref) => ref && !ref.contains(event.target),
      );
      if (isOutside) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const pulldata = useCallback(
    async (userId) => {
      try {
        const response = await instance.get(`/cloudUserData.php?u=${userId}`);
        const fetchedUser = response.data[0];

        // 1. Update React's internal state to trigger the re-render with new data
        setUser(fetchedUser);

        // 2. Ensure MMKV storage is explicitly updated (backup for custom hook reliability)
        storage.set("user.Feed", JSON.stringify(fetchedUser));

        setError(null);
      } catch (e) {
        if (e.response) {
          setError("Failed to refresh user data: " + e.response.data.message);
          console.error(
            "Failed to refresh user data: ",
            e.response.data.message,
          );
        } else if (e.request) {
          setError("Error: No response received from server.");
          console.error("Error: ", e.request);
        } else {
          setError("Error: " + e.message);
          console.error("Error: ", e.message);
        }
      } finally {
      }
    },
    [setUser],
  ); // Added setUser to dependency array

  const login = async (email, password) => {
    setLoading(true);
    setError("");
    const data = new FormData();
    data.append("email", email);
    data.append("password", md5(password));

    try {
      const response = await instance.post("/checkAccount.php", data);
      if (response.data[0].errResponse === "3") {
        setError("Incorrect email and password combination.");
      } else if (response.data[0].errResponse === "1") {
        setError("This account has been locked. Please contact support.");
      } else {
        await pulldata(response.data[0].errResponse);
        setIsLoggedIn(true);
        setCurrentView(views.dashboard);
        setError(null);
      }
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(
          "An error occurred during login. Please try again: " +
            e.response.data.message,
        );
        console.error(
          "An error occurred during login. Please try again: ",
          e.response.data.message,
        );
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    storage.clearAll();
    setIsLoggedIn(false);
    setIsMenuOpen(false);
    setCurrentView(views.login);
    return;
  };

  const handleViewChange = (newView, submenu, params = {}) => {
    setCurrentView(newView);
    if (params.lead) setActiveLead(params.lead);
    if (params.shortname) setActiveResponder(params.shortname);
    storage.set(
      "user.Last.Sub.View",
      JSON.stringify([
        {
          lastView: newView,
          submenu: submenu === undefined ? newView : submenu,
        },
      ]),
    );
  };

  const handleImageChange = async (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      setIsImageLoading(true);
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append("owner", user?.app_id);
      formData.append("imageName", file.name);
      formData.append("imageData", file);

      try {
        await instance.post("/uploadInvestorPhoto.php", formData);
        setError(null);
      } catch (e) {
        if (e.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          setError("Error updating user photo: " + e.response.data.message);
          console.error("Error updating user photo: ", e.response.data.message);
        } else if (e.request) {
          // The request was made but no response was received
          setError("Error: No response received from server.");
          console.error("Error: ", e.request);
        } else {
          // Something happened in setting up the request that triggered an Error
          setError("Error: " + e.message);
          console.error("Error: ", e.message);
        }
      } finally {
        setIsImageLoading(false);
        await pulldata(user?.app_id);
      }
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("owner", user?.app_id);
    formData.append("email", user?.app_email);
    try {
      await instance.post("/deleteDeactivate.php", formData);
      logout();
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error deactivating user account: " + e.response.data.message);
        console.error(
          "Error deactivating user account: ",
          e.response.data.message,
        );
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      await pulldata(user?.app_id);
    }
  };

  const renderContent = () => {
    switch (currentView) {
      case views.dashboard:
        return (
          <Dashboard user={user} pulldata={() => pulldata(user?.app_id)} />
        );
      case views.admin:
        return <Admin user={user} />;
      case "skipTraceHistory":
        return <HistoryPagesSkip view={currentView} user={user} />;
      case "leadQueryHistory":
        return <HistoryPages view={currentView} user={user} />;
      case "sellerleads":
        return (
          <SellerPages
            onViewChange={handleViewChange}
            user={user}
            pulldata={() => pulldata(user?.app_id)}
          />
        );
      case "contractform":
        return (
          <ContractForm
            onViewChange={handleViewChange}
            user={user}
            pulldata={() => pulldata(user?.app_id)}
            leads={activeLead}
          />
        );
      case views.editLead:
        return (
          <EditLeadPage
            onViewChange={handleViewChange}
            leads={activeLead}
            user={user}
            pulldata={() => pulldata(user?.app_id)}
          />
        );
      case views.sellerEditScreen:
        return (
          <EditLeadPageBlank
            onViewChange={handleViewChange}
            leads={activeLead}
            user={user}
            pulldata={() => pulldata(user?.app_id)}
          />
        );
      case "sellerresponders":
        return <SellerResponders onViewChange={handleViewChange} user={user} />;
      case "sellerQS":
        return <QuickScorebaord />;
      case "sellerrespondersview":
        return (
          <SellerRespondersView
            onViewChange={handleViewChange}
            shortname={activeResponder}
            user={user}
          />
        );
      case "investorleads":
        return <InvestorPages user={user} />;
      case "investorresponders":
        return (
          <InvestorResponders onViewChange={handleViewChange} user={user} />
        );
      case "investorrespondersview":
        return (
          <InvestorRespondersView
            onViewChange={handleViewChange}
            shortname={activeResponder}
            user={user}
          />
        );
      case "skiptrace":
        return (
          <Skiptracing user={user} pulldata={() => pulldata(user?.app_id)} />
        );
      case "myInformation":
        return (
          <MyInformation pulldata={() => pulldata(user?.app_id)} user={user} />
        );
      case "myCompany":
        return (
          <MyCompany pulldata={() => pulldata(user?.app_id)} user={user} />
        );
      case "contractOptions":
        return (
          <ContractOptions
            pulldata={() => pulldata(user?.app_id)}
            user={user}
          />
        );
      case "myInvestorProfile":
        return (
          <MyInvestor pulldata={() => pulldata(user?.app_id)} user={user} />
        );
      case views.sellerSites:
        return (
          <SqueezSite pulldata={() => pulldata(user?.app_id)} user={user} />
        );
      case views.training:
        return <Training />;
      case views.ecommerce:
        return <Ecommerce />;
      case views.support:
        return <SupportPage />;
      case views.links:
        return <Links view={currentView} />;
      default:
        return (
          <Dashboard user={user} pulldata={() => pulldata(user?.app_id)} />
        );
    }
  };

  if (!isLoggedIn) {
    return (
      <AuthForm
        onAuthSubmit={login}
        error={error}
        loading={loading}
        onSwitchView={() => {}}
        secondaryText=""
        buttonText="Log In"
        title="Sign in to your account"
      />
    );
  }

  return (
    <div className="flex flex-col h-screen bg-white dark:bg-gray-900">
      <nav className="flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 shadow-md sticky top-0 z-40 border-b border-gray-200 dark:border-gray-700">
        <a href="/" className="flex items-center gap-3">
          <img
            src="./images/logo-dark.png"
            alt="logo"
            className="h-14 dark:hidden"
          />
          <img
            src="./images/logo-light.png"
            alt="logo"
            className="h-14 hidden dark:inline"
          />
          <div className="hidden sm:flex flex-col">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              BigDogTools
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Real Estate Platform
            </p>
          </div>
        </a>
        <div
          className="flex items-center gap-3 relative"
          ref={(el) => (menuRef.current[0] = el)}
        >
          <div className="hidden sm:flex flex-col items-end mr-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {user?.app_name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {user?.app_email}
            </p>
          </div>
          <img
            src={
              `${url}/../mobile/vestors/${user?.images}` ||
              "https://placehold.co/160x160/E2E8F0/4A5568?text=No+Image"
            }
            alt="User Profile"
            className="h-12 w-12 rounded-full object-cover border-2 border-blue-200 dark:border-blue-900/50 shadow-md cursor-pointer transition-transform hover:scale-105"
            onClick={() => setIsProfileModalOpen(true)}
          />
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`p-2 rounded-lg transition-colors ${
              isMenuOpen
                ? "bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            }`}
          >
            <svg
              className="w-5 h-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="4" x2="20" y1="12" y2="12"></line>
              <line x1="4" x2="20" y1="6" y2="6"></line>
              <line x1="4" x2="20" y1="18" y2="18"></line>
            </svg>
          </button>
          {isMenuOpen && (
            <div className="absolute top-full right-0 mt-2 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-2 z-50">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {user?.app_name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.app_email}
                </p>
              </div>
              <a
                href="/#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsProfileModalOpen(true);
                  setIsMenuOpen(false);
                }}
                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clipRule="evenodd"
                  />
                </svg>
                View Profile & Credits
              </a>
              <a
                href="/#"
                onClick={(e) => {
                  e.preventDefault();
                  logout();
                }}
                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                    clipRule="evenodd"
                  />
                </svg>
                Logout
              </a>
              <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
              <a
                href="/#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsDeleteModalOpen(true);
                  setIsMenuOpen(false);
                }}
                className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Delete Account
              </a>
            </div>
          )}
        </div>
      </nav>
      {/* Sunset Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-300 dark:border-amber-700 px-4 py-2 text-center">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          <strong>Important Notice:</strong> This app is being sunsetted and will transition to the{" "}
          <strong>FlippingPro app</strong>, available in the App Store. All future support will be through{" "}
          <a
            href="mailto:support@FlippingPro.com"
            className="underline font-semibold hover:text-amber-900 dark:hover:text-amber-100"
          >
            support@FlippingPro.com
          </a>
          .
        </p>
      </div>
      <div className="flex flex-1 overflow-hidden">
        <Navbar
          onViewChange={handleViewChange}
          currentView={currentView}
          user={user}
        />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          {renderContent()}
        </main>
      </div>
      <AIAssistantChat />
      <footer className="w-full bg-white dark:bg-gray-800 p-4 text-center border-t border-gray-200 dark:border-gray-700 dark:text-gray-200">
        <p className="text-sm text-gray-600 dark:text-gray-200">
          &copy; {moment().format("YYYY")} Love American Homes. All rights
          Reserved.
        </p>
      </footer>

      <Modal
        show={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        title="My Profile"
        maxWidth="max-w-sm"
        icon={
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
              clipRule="evenodd"
            />
          </svg>
        }
        iconColor="blue"
      >
        <div className="flex flex-col items-center space-y-5 py-4">
          {/* Profile Image */}
          <div className="relative">
            <img
              src={
                `${url}/../mobile/vestors/${user?.images}` ||
                "https://placehold.co/160x160/E2E8F0/4A5568?text=No+Image"
              }
              alt="User Profile"
              className="h-32 w-32 rounded-full object-cover border-4 border-blue-200 dark:border-blue-900/50 shadow-lg"
            />
          </div>

          {/* Profile Info */}
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {user?.app_name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {user?.app_email}
            </p>
          </div>

          {/* Change Image Button */}
          {isImageLoading ? (
            <div className="flex justify-center py-2">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <label className="cursor-pointer bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-500 text-sm font-semibold transition-colors flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
              </svg>
              Change Image
              <input
                type="file"
                name="logo"
                onChange={handleImageChange}
                accept="image/*"
                className="hidden"
              />
            </label>
          )}

          {/* Credits Section */}
          <div className="w-full space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
              Available Credits
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-900/30">
                <p className="text-xs text-green-600 dark:text-green-400 font-semibold">
                  AI Query
                </p>
                <p className="text-lg font-bold text-green-700 dark:text-green-300 mt-1">
                  {Math.max(
                    0,
                    parseInt(user?.credits || "0", 10) || 0,
                  ).toLocaleString()}
                </p>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-900/30">
                <p className="text-xs text-orange-600 dark:text-orange-400 font-semibold">
                  Skiptrace
                </p>
                <p className="text-lg font-bold text-orange-700 dark:text-orange-300 mt-1">
                  {Math.max(
                    0,
                    parseInt(user?.skip_credits || "0", 10) || 0,
                  ).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        show={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Account"
        icon={
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        }
        iconColor="red"
        footer={
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
            >
              {loading ? "Deleting..." : "Confirm Deletion"}
            </button>
          </div>
        }
      >
        <div className="space-y-4 py-4">
          <div className="flex gap-4">
            <div className="flex-shrink-0 mt-0.5">
              <svg
                className="w-6 h-6 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Are you sure?
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                You are about to permanently delete your Big Dog Tools Account.
                This action is not reversible. Once the deactivation is
                complete, you will be logged out immediately.
              </p>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
